import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Dog, Shield, Home, BookOpen, Users, LogOut, Settings,
  BarChart3, ChevronDown, PawPrint, Heart, Trophy, MessageSquare, Calendar, Flame
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = "https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=130";

export const OwnerLayout = ({ children }) => {
  const { user, activeDog, logout, selectDog } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dogs, setDogs] = React.useState([]);

  React.useEffect(() => {
    if (user) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dogs/owner/${user.id}`)
        .then(res => res.json())
        .then(data => {
          setDogs(data);
          if (data.length > 0 && !activeDog) {
            selectDog(data[0]);
          }
        })
        .catch(console.error);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/owner/dashboard', label: 'Dashboard', icon: Home },
    { path: '/owner/series', label: 'Training', icon: BookOpen },
    { path: '/owner/journal', label: 'Journal', icon: Flame },
    { path: '/owner/dogs', label: 'My Dogs', icon: PawPrint },
    { path: '/owner/family', label: 'Family', icon: Users },
    { path: '/owner/community', label: 'Community', icon: Heart },
    { path: '/owner/sessions', label: 'Book Session', icon: Calendar },
    { path: '/owner/leaderboard', label: 'Leaderboard', icon: Trophy },
    { path: '/owner/messages', label: 'Messages', icon: MessageSquare },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/owner/dashboard" className="flex items-center">
              <img src={LOGO_URL} alt="Edu Paws" className="h-10 w-auto" />
            </Link>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full font-body font-medium text-sm transition-all
                    ${isActive(item.path) 
                      ? 'bg-edupaws-navy text-white shadow-[0_2px_0_#2D3B47]' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-edupaws-navy'
                    }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Dog Selector */}
              {dogs.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      data-testid="dog-selector"
                      className="flex items-center gap-2 bg-edupaws-sage/10 px-3 py-2 rounded-full transition-colors hover:bg-edupaws-sage/20"
                    >
                      {activeDog?.photo_url ? (
                        <img src={activeDog.photo_url} alt={activeDog.name} className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 bg-edupaws-sage/30 rounded-full flex items-center justify-center">
                          <PawPrint className="w-4 h-4 text-edupaws-navy" />
                        </div>
                      )}
                      <span className="font-body font-medium text-edupaws-navy hidden sm:block">
                        {activeDog?.name || 'Select Dog'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-edupaws-navy/50" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {dogs.map((dog) => (
                      <DropdownMenuItem
                        key={dog.id}
                        onClick={() => selectDog(dog)}
                        className={`cursor-pointer ${activeDog?.id === dog.id ? 'bg-edupaws-sage/10' : ''}`}
                      >
                        {dog.photo_url ? (
                          <img src={dog.photo_url} alt={dog.name} className="w-6 h-6 rounded-full object-cover mr-2" />
                        ) : (
                          <PawPrint className="w-4 h-4 mr-2 text-edupaws-sage" />
                        )}
                        {dog.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    data-testid="user-menu"
                    className="flex items-center gap-2 hover:bg-gray-100 px-3 py-2 rounded-full transition-colors"
                  >
                    <div className="w-8 h-8 bg-edupaws-navy rounded-full flex items-center justify-center">
                      <span className="font-heading font-bold text-white text-sm">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled className="text-gray-500">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="user-menu-settings"
                    onClick={() => navigate('/owner/settings')} 
                    className="cursor-pointer"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile Nav */}
          <nav className="lg:hidden flex items-center gap-1 mt-3 pb-1 overflow-x-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-body text-sm font-medium whitespace-nowrap transition-all
                  ${isActive(item.path) 
                    ? 'bg-edupaws-navy text-white' 
                    : 'text-gray-500 hover:bg-gray-100'
                  }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {children}
      </main>
    </div>
  );
};

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: Home, exact: true },
    { path: '/admin/series', label: 'Content', icon: BookOpen },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-edupaws-navy text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/admin" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Edu Paws" className="h-10 w-auto brightness-0 invert" />
              <span className="bg-edupaws-sage text-white text-xs px-2 py-1 rounded-full font-body font-medium">ADMIN</span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-body font-medium transition-all
                    ${isActive(item.path, item.exact) 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="hidden sm:block">{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  data-testid="admin-user-menu"
                  className="flex items-center gap-2 hover:bg-white/10 px-3 py-2 rounded-full transition-colors"
                >
                  <div className="w-8 h-8 bg-edupaws-sage rounded-full flex items-center justify-center">
                    <span className="font-heading font-bold text-white text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="font-body font-medium text-white hidden sm:block">
                    {user?.name}
                  </span>
                  <ChevronDown className="w-4 h-4 text-white/70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled className="text-gray-500">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {children}
      </main>
    </div>
  );
};
